"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _Amount = require("../../utils/Amount");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
const BrixelAmount = /*#__PURE__*/(0, _react.forwardRef)(({
  allowDecimals = true,
  allowNegativeValue = true,
  decimalScale,
  decimalSeparator: _decimalSeparator,
  decimalsLimit,
  defaultValue,
  disableAbbreviations = false,
  disableGroupSeparators = false,
  disabled = false,
  fixedDecimalLength,
  groupSeparator: _groupSeparator,
  id,
  intlConfig,
  max,
  maxLength: userMaxLength,
  min,
  name,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  onKeyUp,
  onValueChange,
  placeholder,
  prefix,
  step,
  suffix,
  transformRawValue,
  value: userValue,
  ...props
}, ref) => {
  if (_decimalSeparator && (0, _Amount.isNumber)(_decimalSeparator)) {
    throw new Error('decimalSeparator cannot be a number');
  }
  if (_groupSeparator && (0, _Amount.isNumber)(_groupSeparator)) {
    throw new Error('groupSeparator cannot be a number');
  }
  const localeConfig = (0, _react.useMemo)(() => (0, _Amount.getLocaleConfig)(intlConfig), [intlConfig]);
  const decimalSeparator = _decimalSeparator || localeConfig.decimalSeparator || '';
  const groupSeparator = _groupSeparator || localeConfig.groupSeparator || '';
  if (decimalSeparator && groupSeparator && decimalSeparator === groupSeparator && disableGroupSeparators === false) {
    throw new Error('decimalSeparator cannot be the same as groupSeparator');
  }
  const formatValueOptions = {
    decimalSeparator,
    groupSeparator,
    disableGroupSeparators,
    intlConfig,
    prefix: prefix || localeConfig.prefix,
    suffix: suffix
  };
  const cleanValueOptions = {
    decimalSeparator,
    groupSeparator,
    allowDecimals,
    decimalsLimit: decimalsLimit || fixedDecimalLength || 2,
    allowNegativeValue,
    disableAbbreviations,
    prefix: prefix || localeConfig.prefix,
    transformRawValue
  };
  const formattedStateValue = defaultValue !== undefined && defaultValue !== null ? (0, _Amount.formatValue)({
    ...formatValueOptions,
    decimalScale,
    value: String(defaultValue)
  }) : userValue !== undefined && userValue !== null ? (0, _Amount.formatValue)({
    ...formatValueOptions,
    decimalScale,
    value: String(userValue)
  }) : '';
  const [stateValue, setStateValue] = (0, _react.useState)(formattedStateValue);
  const [dirty, setDirty] = (0, _react.useState)(false);
  const [cursor, setCursor] = (0, _react.useState)(0);
  const [changeCount, setChangeCount] = (0, _react.useState)(0);
  const [lastKeyStroke, setLastKeyStroke] = (0, _react.useState)(null);
  const inputRef = (0, _react.useRef)(null);
  (0, _react.useImperativeHandle)(ref, () => inputRef.current);
  /**
   * Process change in value
   */
  const processChange = (value, selectionStart) => {
    setDirty(true);
    const {
      modifiedValue,
      cursorPosition
    } = (0, _Amount.repositionCursor)({
      selectionStart,
      value,
      lastKeyStroke,
      stateValue,
      groupSeparator
    });
    const stringValue = (0, _Amount.cleanValue)({
      value: modifiedValue,
      ...cleanValueOptions
    });
    if (userMaxLength && stringValue.replace(/-/g, '').length > userMaxLength) {
      return;
    }
    if (stringValue === '' || stringValue === '-' || stringValue === decimalSeparator) {
      onValueChange && onValueChange(undefined, name, {
        float: null,
        formatted: '',
        value: ''
      });
      setStateValue(stringValue);
      return;
    }
    const stringValueWithoutSeparator = decimalSeparator ? stringValue.replace(decimalSeparator, '.') : stringValue;
    const numberValue = parseFloat(stringValueWithoutSeparator);
    const formattedValue = (0, _Amount.formatValue)({
      value: stringValue,
      ...formatValueOptions
    });
    if (cursorPosition !== undefined && cursorPosition !== null) {
      // Prevent cursor jumping
      let newCursor = cursorPosition + (formattedValue.length - value.length);
      newCursor = newCursor <= 0 ? prefix ? prefix.length : 0 : newCursor;
      setCursor(newCursor);
      setChangeCount(changeCount + 1);
    }
    setStateValue(formattedValue);
    if (onValueChange) {
      const values = {
        float: numberValue,
        formatted: formattedValue,
        value: stringValue
      };
      onValueChange(stringValue, name, values);
    }
  };
  /**
   * Handle change event
   */
  const handleOnChange = event => {
    const {
      target: {
        value,
        selectionStart
      }
    } = event;
    processChange(value, selectionStart);
    onChange && onChange(event);
  };
  /**
   * Handle focus event
   */
  const handleOnFocus = event => {
    onFocus && onFocus(event);
    return stateValue ? stateValue.length : 0;
  };
  /**
   * Handle blur event
   *
   * Format value by padding/trimming decimals if required by
   */
  const handleOnBlur = event => {
    const {
      target: {
        value
      }
    } = event;
    const valueOnly = (0, _Amount.cleanValue)({
      value,
      ...cleanValueOptions
    });
    if (valueOnly === '-' || !valueOnly) {
      setStateValue('');
      onBlur && onBlur(event);
      return;
    }
    const fixedDecimals = (0, _Amount.fixedDecimalValue)(valueOnly, decimalSeparator, fixedDecimalLength);
    const newValue = (0, _Amount.padTrimValue)(fixedDecimals, decimalSeparator, decimalScale !== undefined ? decimalScale : fixedDecimalLength);
    const numberValue = parseFloat(newValue.replace(decimalSeparator, '.'));
    const formattedValue = (0, _Amount.formatValue)({
      ...formatValueOptions,
      value: newValue
    });
    if (onValueChange) {
      onValueChange(newValue, name, {
        float: numberValue,
        formatted: formattedValue,
        value: newValue
      });
    }
    setStateValue(formattedValue);
    onBlur && onBlur(event);
  };
  /**
   * Handle key down event
   *
   * Increase or decrease value by step
   */
  const handleOnKeyDown = event => {
    const {
      key
    } = event;
    setLastKeyStroke(key);
    if (step && (key === 'ArrowUp' || key === 'ArrowDown')) {
      event.preventDefault();
      setCursor(stateValue.length);
      const currentValue = parseFloat(userValue !== undefined && userValue !== null ? String(userValue).replace(decimalSeparator, '.') : (0, _Amount.cleanValue)({
        value: stateValue,
        ...cleanValueOptions
      })) || 0;
      const newValue = key === 'ArrowUp' ? currentValue + step : currentValue - step;
      if (min !== undefined && +newValue < +min) {
        return;
      }
      if (max !== undefined && +newValue > +max) {
        return;
      }
      const fixedLength = String(step).includes('.') ? Number(String(step).split('.')[1].length) : undefined;
      processChange(String(fixedLength ? newValue.toFixed(fixedLength) : newValue).replace('.', decimalSeparator));
    }
    onKeyDown && onKeyDown(event);
  };
  /**
   * Handle key up event
   *
   * Move cursor if there is a suffix to prevent user typing past suffix
   */
  const handleOnKeyUp = event => {
    const {
      key,
      currentTarget: {
        selectionStart
      }
    } = event;
    if (key !== 'ArrowUp' && key !== 'ArrowDown' && stateValue !== '-') {
      const currentSuffix = (0, _Amount.getSuffix)(stateValue, {
        groupSeparator,
        decimalSeparator
      });
      if (currentSuffix && selectionStart && selectionStart > stateValue.length - currentSuffix.length) {
        /* istanbul ignore else */
        if (inputRef.current) {
          const newCursor = stateValue.length - currentSuffix.length;
          inputRef.current.setSelectionRange(newCursor, newCursor);
        }
      }
    }
    onKeyUp && onKeyUp(event);
  };
  (0, _react.useEffect)(() => {
    // prevent cursor jumping if editing value
    if (dirty && stateValue !== '-' && inputRef.current && document.activeElement === inputRef.current) {
      inputRef.current.setSelectionRange(cursor, cursor);
    }
  }, [stateValue, cursor, inputRef, dirty, changeCount]);
  /**
   * If user has only entered "-" or decimal separator,
   * keep the char to allow them to enter next value
   */
  const getRenderValue = () => {
    if (userValue !== undefined && userValue !== null && stateValue !== '-' && (!decimalSeparator || stateValue !== decimalSeparator)) {
      return (0, _Amount.formatValue)({
        ...formatValueOptions,
        decimalScale: dirty ? undefined : decimalScale,
        value: String(userValue)
      });
    }
    return stateValue;
  };
  const inputProps = {
    className: '',
    type: 'text',
    inputMode: 'decimal',
    id,
    name,
    onChange: handleOnChange,
    onBlur: handleOnBlur,
    onFocus: handleOnFocus,
    onKeyDown: handleOnKeyDown,
    onKeyUp: handleOnKeyUp,
    placeholder,
    disabled,
    value: getRenderValue(),
    ref: inputRef,
    onWheel: e => e.currentTarget.blur(),
    ...props
  };
  return /*#__PURE__*/_react.default.createElement("input", {
    "data-testid": "inputText",
    ...inputProps
  });
});
BrixelAmount.displayName = 'BrixelAmount';
var _default = exports.default = BrixelAmount;